import { useAppDispatch, useAppSelector } from '@app/store';
import React, { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { GeneratedContentCard, GenerationHeader } from '../components';
import { GeneratedContentCardPlaceholder } from '../components/generated-content-card';
import { ContentType } from '../domain';
import { selectContentComposerContentHistory } from '../selector';

type ContentGenerationProps = {
  contentType: ContentType;
};

// TODO(post-event-enable-creator-lab-consolidation): Remove feature flag once fully rolled out
export const ContentGeneration = ({ contentType }: ContentGenerationProps) => {
  const dispatch = useAppDispatch();

  // Get enriched history items for this content type
  const history = useAppSelector(state =>
    selectContentComposerContentHistory(state),
  )[contentType];

  const hasNextPage = history?.cursor !== null;
  const isLoading = history?.status === 'processing';
  const latestHistoryItemId = useRef(history?.history[0]?.id ?? null);

  // Track if we've already fetched for this "in view" session
  // Reset when placeholder goes out of view
  const hasFetchedRef = useRef(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Intersection observer for infinite scroll
  const { ref: placeholderRef, inView } = useInView({
    threshold: 0.1, // Trigger when 10% visible
    triggerOnce: false, // We want to track when it goes out and comes back
  });

  // Reset fetch flag when placeholder goes out of view
  useEffect(() => {
    if (!inView) {
      hasFetchedRef.current = false;
    }
  }, [inView]);

  // Fetch next page when placeholder comes into view
  useEffect(() => {
    // Only fetch if:
    // 1. Placeholder is in view
    // 2. Not already loading
    // 3. There's a next page (cursor exists)
    // 4. Haven't already fetched in this "in view" session
    if (inView && !isLoading && hasNextPage && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch({
        type: '[content-composer] fetch content history next page',
        payload: { contentType },
      });
    }
  }, [inView, isLoading, hasNextPage, contentType, dispatch]);

  useEffect(() => {
    const currentFirstItemId = history?.history[0]?.id;
    if (
      currentFirstItemId &&
      currentFirstItemId !== latestHistoryItemId.current &&
      latestHistoryItemId.current !== null
    ) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      latestHistoryItemId.current = currentFirstItemId;
    }
  }, [history?.history]);

  return (
    <div
      ref={scrollContainerRef}
      className="h-full overflow-y-auto px-4 flex flex-col gap-4 [scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]"
    >
      {/* Generation Header */}
      <GenerationHeader contentType={contentType} />

      {/* Generated Content Cards */}
      <div className="flex flex-col gap-4">
        {history?.history?.map(item => (
          <GeneratedContentCard key={item.id} item={item} />
        ))}
        {hasNextPage && (
          <div ref={placeholderRef}>
            <GeneratedContentCardPlaceholder />
          </div>
        )}
      </div>
    </div>
  );
};
